/**
 * What a permission module is CALLED, as opposed to what it is keyed by.
 *
 * The catalogue's module strings are identifiers — they are half of
 * `permission.code`, they are what routePermissions.ts matches on server-side,
 * and every existing grant in `role_permission` hangs off them. They are also,
 * historically, the names the role editor put on screen, which is why an
 * operator ticking a box saw "Partner Management" for the screen the sidebar
 * calls "Partners", and "Finance & Settlement" for the one it calls "Billing".
 *
 * This maps identifier → navbar wording, for DISPLAY ONLY.
 *
 * ── Why not just rename the modules ─────────────────────────────────────────
 *
 * Because the module string is load-bearing in four places at once: the
 * `permission` rows, the `role_permission` grants pointing at their ids, the
 * route→permission table the API enforces with, and the portal's own
 * navPermissions map. Renaming means a data migration that repoints every grant
 * (~40 roles hold Reports alone) executed in lockstep with a code deploy, in
 * every environment — and if the DB and routePermissions.ts ever disagree while
 * ENABLE_ROUTE_AUTHZ is enforcing, every protected route 403s for everyone.
 *
 * A label map buys the same visible result for none of that risk, and it keeps
 * working when somebody renames a sidebar item next month: you edit one line
 * here instead of migrating a database.
 *
 * ⚠️ DISPLAY ONLY. Never key state, build a permission code, or send an API
 * payload from a label — the role editor's own state is keyed by the CANONICAL
 * module name, and `flattenPermissions` on the server rebuilds the code from it.
 * Labelling a key would silently write grants nobody can hold.
 */

/**
 * Labels shared by both portals, keyed `side:Module` or `side:Module/SubModule` —
 * the same encoding the catalogue uses, so a key here is greppable against a
 * permission code.
 *
 * Only DIVERGENCES are listed. A module whose catalogue name already matches the
 * sidebar (Dashboard, Deal Codes, Dropnet, Partner Mapping, Approval Queue,
 * Settings, Travel and its three children) is deliberately absent — repeating it
 * would be one more thing to keep in sync for no gain.
 */
const LABELS = {
  // ── Admin side ────────────────────────────────────────────────────────────
  'admin:Partner Management': 'Partners',
  'admin:Branding & Theming': 'Branding',
  'admin:Feature Flags': 'Features',
  // The sidebar has ONE item, "Users & Roles", covering two modules. Splitting
  // the label the way the modules split is more useful than printing the same
  // words twice: the whole point of two modules is that they grant separately.
  'admin:User Management': 'Users',
  'admin:Role Management': 'Roles',
  'admin:Revenue/Markup Rules': 'Markups',
  // Parent "Suppliers" with a child also called "Suppliers" looks like a
  // mistake, but it is exactly what the sidebar shows — Suppliers > Suppliers.
  'admin:Suppliers/Supplier Config': 'Suppliers',

  // ── CRM side ──────────────────────────────────────────────────────────────
  'crm:Bookings/Booking Management': 'Bookings',
  // One module governing TWO sidebar items (Update Hotel and Update Flight),
  // so neither name alone is honest. Both are named rather than inventing a
  // third word, because a role granted this can do both and should see that.
  'crm:Bookings/Manual Booking': 'Update Hotel / Update Flight',
  'crm:Post-Booking Servicing': 'Requests',
  'crm:Customer Management': 'Customers',
  'crm:Finance & Settlement': 'Billing',
  'crm:Communication Hub': 'Communications',
  'crm:Reports & Analytics': 'Reports',
};

/**
 * Where the two portals use different words for the same module.
 *
 * A role belongs to one `system` — 'zenvoya' roles are held by staff working in
 * the admin portal, 'partner' roles by people in the partner portal — so the
 * role editor can and should label each with the sidebar its holders will
 * actually see. Anything not overridden falls through to {@link LABELS}.
 */
const PARTNER_OVERRIDES = {
  // Partner Settings has a single "Markup & Fees" item covering both.
  'admin:Revenue/Markup Rules': 'Markup & Fees',
  'admin:Revenue/Promo Codes': 'Promotions',
};

/** `side:Module` / `side:Module/SubModule`, matching the catalogue encoding. */
export const moduleKeyOf = (side, module, subModule) =>
  subModule ? `${side}:${module}/${subModule}` : `${side}:${module}`;

/**
 * The sidebar wording for a module, or the module's own name when it already
 * matches (or when nobody has mapped it).
 *
 * Falling back to the canonical name rather than to a placeholder is deliberate:
 * a module added to the catalogue later shows up in the editor under its real
 * name instead of vanishing or rendering blank, which is the failure mode that
 * would be hardest to notice.
 *
 * @param system 'partner' selects the partner portal's wording; anything else
 *               (including undefined) uses the admin portal's.
 */
export const moduleLabel = (side, module, subModule, system) => {
  const key = moduleKeyOf(side, module, subModule);
  if (system === 'partner' && PARTNER_OVERRIDES[key]) return PARTNER_OVERRIDES[key];
  return LABELS[key] || subModule || module;
};

/**
 * A whole permission code or module key rendered for a human, e.g.
 * `admin:Revenue/Markup Rules` → "Revenue › Markups".
 *
 * Used by the 403 screen and the denied-control tooltips, so the words an
 * operator is told to ask an administrator for are the words that administrator
 * will see in the role editor. Printing the raw key there instead is how you get
 * a support thread about a permission nobody can find.
 */
export const describeModuleKey = (moduleKey, system) => {
  if (!moduleKey) return '';
  const [side, rest] = moduleKey.includes(':') ? [moduleKey.slice(0, moduleKey.indexOf(':')), moduleKey.slice(moduleKey.indexOf(':') + 1)] : ['admin', moduleKey];
  const slash = rest.indexOf('/');
  const module = slash === -1 ? rest : rest.slice(0, slash);
  const subModule = slash === -1 ? undefined : rest.slice(slash + 1);

  const leaf = moduleLabel(side, module, subModule, system);
  if (!subModule) return leaf;

  // Parents are shown too, because a bare "Markups" does not tell an
  // administrator which section of the editor to open it from.
  const parent = moduleLabel(side, module, undefined, system);
  return `${parent} › ${leaf}`;
};

/**
 * The label for a module named WITHOUT its side prefix.
 *
 * `POST /app/v1/partners/users/roles` reports a role's permissions as bare
 * top-level module names — it runs `parseModuleKey(...).module` and throws the
 * side away — so the partner portal's role chips have no `admin:`/`crm:` to key
 * on. Both sides are tried; no module name is used on both today (the admin and
 * crm module lists are disjoint), so the answer is unambiguous, and an unknown
 * name falls through to itself.
 *
 * Prefer {@link moduleLabel} wherever the side IS known — this exists for the
 * one API shape that discards it.
 */
export const labelForBareModule = (module, system) => {
  if (!module) return '';
  for (const side of ['admin', 'crm']) {
    const key = moduleKeyOf(side, module);
    if ((system === 'partner' && PARTNER_OVERRIDES[key]) || LABELS[key]) {
      return moduleLabel(side, module, undefined, system);
    }
  }
  return module;
};
